import { useState } from 'react';
import { Modal } from './Modal';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

interface AIDramaApplyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AIDramaApplyModal = ({ isOpen, onClose }: AIDramaApplyModalProps) => {
  const { user, profile } = useAuth();
  const [formData, setFormData] = useState({
    name: profile?.username || '',
    email: user?.email || '',
    portfolio: '',
    tools: '',
    experience: 'beginner',
    projectIdea: '',
    sampleUrl: '',
    motivation: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.motivation.trim()) {
      setError('Please fill in all required fields');
      return;
    }
    if (formData.motivation.length > 500) {
      setError('Motivation must be 500 characters or less');
      return;
    }
    if (formData.projectIdea.length > 500) {
      setError('Project idea must be 500 characters or less');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Build portfolio links array
      const portfolioLinks: string[] = [];
      if (formData.portfolio.trim()) portfolioLinks.push(formData.portfolio.trim());
      if (formData.sampleUrl.trim()) portfolioLinks.push(formData.sampleUrl.trim());

      // Build application reason combining motivation + project idea + tools + experience
      const applicationParts: string[] = [];
      applicationParts.push(`Motivation: ${formData.motivation.trim()}`);
      if (formData.projectIdea.trim()) applicationParts.push(`Project Idea: ${formData.projectIdea.trim()}`);
      if (formData.tools.trim()) applicationParts.push(`AI Tools: ${formData.tools.trim()}`);
      applicationParts.push(`Experience Level: ${formData.experience}`);
      const applicationReason = applicationParts.join('\n\n');

      // Insert into creator_applications table
      if (user?.id) {
        const { data: insertedApp, error: dbError } = await supabase
          .from('creator_applications')
          .insert({
            user_id: user.id,
            status: 'pending',
            portfolio_links: portfolioLinks.length > 0 ? portfolioLinks : null,
            application_reason: applicationReason,
            applied_at: new Date().toISOString(),
          })
          .select('id')
          .maybeSingle();

        if (dbError) {
          // If duplicate, show friendly message
          if (dbError.code === '23505') {
            setError('You have already submitted an application. Please wait for review.');
            setSubmitting(false);
            return;
          }
          console.error('DB insert error:', dbError);
        }

        // Log the submission as an activity entry
        if (insertedApp?.id) {
          await supabase.from('admin_activity_log').insert({
            admin_user_id: user.id,
            application_id: insertedApp.id,
            action: 'submitted',
            previous_status: null,
            new_status: 'pending',
            reason: 'Application submitted by user',
          }).then(() => {}).catch(() => {});
        }
      }

      // Also submit to form endpoint
      const body = new URLSearchParams();
      body.append('name', formData.name.trim());
      body.append('email', formData.email.trim());
      body.append('portfolio', formData.portfolio.trim());
      body.append('tools', formData.tools.trim());
      body.append('experience', formData.experience);
      body.append('projectIdea', formData.projectIdea.trim());
      body.append('sampleUrl', formData.sampleUrl.trim());
      body.append('motivation', formData.motivation.trim());

      await fetch('https://readdy.ai/api/form/d6aaiq7mvg9ih2c7afdg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString()
      }).catch(() => {
        // Form endpoint failure is non-critical since we saved to DB
      });

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData({
        name: profile?.username || '',
        email: user?.email || '',
        portfolio: '',
        tools: '',
        experience: 'beginner',
        projectIdea: '',
        sampleUrl: '',
        motivation: ''
      });
      setSubmitted(false);
      setError('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="" size="large">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="-mx-6 -mt-6 mb-6 px-6 py-5 bg-gradient-to-r from-orange-500 via-rose-500 to-amber-500 rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <i className="ri-robot-2-line text-2xl text-white"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Apply as AI Drama Creator</h2>
              <p className="text-white/80 text-sm">Join our community of AI-powered storytellers</p>
            </div>
          </div>
        </div>

        {submitted ? (
          <div className="text-center py-10">
            <div className="w-20 h-20 mx-auto mb-5 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
              <i className="ri-check-line text-white text-4xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Thank you for applying! We&apos;ll review your application and get back to you within 3-5 business days.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 max-w-sm mx-auto mb-6">
              <div className="flex items-center gap-2 text-amber-700 text-sm">
                <i className="ri-mail-check-line text-lg"></i>
                <span>A confirmation will be sent to <strong>{formData.email}</strong></span>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-rose-500 text-white font-medium rounded-lg hover:from-orange-600 hover:to-rose-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Done
            </button>
          </div>
        ) : (
          <form
            id="ai-drama-apply-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600 flex items-center gap-2">
                <i className="ri-error-warning-line"></i>{error}
              </div>
            )}

            {/* Info Banner */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <i className="ri-lightbulb-flash-line text-orange-500 text-xl mt-0.5"></i>
                <div>
                  <h4 className="text-sm font-semibold text-orange-800 mb-1">What is an AI Drama Creator?</h4>
                  <p className="text-xs text-orange-700 leading-relaxed">
                    AI Drama Creators use cutting-edge AI tools like Sora, Runway, and ElevenLabs to produce original short drama content. 
                    As a verified creator, you&apos;ll get a creator badge, featured placement, and access to exclusive tools and community resources.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <i className="ri-user-line mr-1"></i>Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Your name or creator alias"
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <i className="ri-mail-line mr-1"></i>Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <i className="ri-links-line mr-1"></i>Portfolio / Website
                </label>
                <input
                  type="url"
                  name="portfolio"
                  value={formData.portfolio}
                  onChange={(e) => handleChange('portfolio', e.target.value)}
                  placeholder="https://your-portfolio.com"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <i className="ri-video-line mr-1"></i>Sample Work URL
                </label>
                <input
                  type="url"
                  name="sampleUrl"
                  value={formData.sampleUrl}
                  onChange={(e) => handleChange('sampleUrl', e.target.value)}
                  placeholder="Link to your best AI drama"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <i className="ri-tools-line mr-1"></i>AI Tools You Use
              </label>
              <input
                type="text"
                name="tools"
                value={formData.tools}
                onChange={(e) => handleChange('tools', e.target.value)}
                placeholder="e.g., Sora, Runway Gen-4, ElevenLabs, Midjourney"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <i className="ri-bar-chart-line mr-1"></i>Experience Level <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'beginner', label: 'Beginner', desc: 'Just starting out', icon: 'ri-seedling-line' },
                  { value: 'intermediate', label: 'Intermediate', desc: '1-5 projects', icon: 'ri-plant-line' },
                  { value: 'advanced', label: 'Advanced', desc: '5+ projects', icon: 'ri-tree-line' }
                ].map(level => (
                  <button
                    key={level.value}
                    type="button"
                    onClick={() => handleChange('experience', level.value)}
                    className={`p-3 rounded-lg border-2 transition-all cursor-pointer text-left ${
                      formData.experience === level.value
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <i className={`${level.icon} ${formData.experience === level.value ? 'text-orange-500' : 'text-gray-400'}`}></i>
                      <span className={`text-sm font-medium ${formData.experience === level.value ? 'text-orange-700' : 'text-gray-700'}`}>
                        {level.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{level.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <i className="ri-lightbulb-line mr-1"></i>Project Idea
              </label>
              <textarea
                name="projectIdea"
                value={formData.projectIdea}
                onChange={(e) => handleChange('projectIdea', e.target.value)}
                placeholder="Briefly describe an AI drama project you'd like to create..."
                rows={3}
                maxLength={500}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none text-sm"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{formData.projectIdea.length}/500</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <i className="ri-heart-line mr-1"></i>Why do you want to join? <span className="text-red-500">*</span>
              </label>
              <textarea
                name="motivation"
                value={formData.motivation}
                onChange={(e) => handleChange('motivation', e.target.value)}
                placeholder="Tell us what excites you about AI drama creation and what you hope to achieve..."
                rows={3}
                maxLength={500}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none text-sm"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{formData.motivation.length}/500</p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-rose-500 text-white font-medium rounded-lg hover:from-orange-600 hover:to-rose-600 transition-colors cursor-pointer whitespace-nowrap text-sm flex items-center gap-2 disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    Submit Application
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default AIDramaApplyModal;
